/* @@REV_ENGINE_START@@ — extracted by qa/merge_props.js together with the merge engine. */
/* ══ v14 (W2): CHANGE COUNTERS AND TOMBSTONES — derived at SAVE, from ONE choke point ═════
   ⚠ NOTHING CALLS THIS YET — the wiring into the save path lands in W4 alongside writer
   identity, which the launcher supplies. DO NOT SWEEP IT: qa/merge_props.js extracts and
   exercises this block, so deleting it as unreferenced would take those tests with it.

   HIS DECISION, 2026-08-04 (Way A). The locked scope said "every mutation site bumping it",
   which the recon measured at ~150 `markDirty()` sites. He chose ONE choke point instead,
   and it is strictly better rather than merely cheaper:
     · ONE site, so a missed mutation site is structurally impossible rather than unlikely —
       which removes the exact hazard the scope flagged (a missed site is a record that
       silently loses every collision, and no test can tell you which one you missed);
     · NO FALSE BUMPS — per-site bumping raises a rev when a value is typed and then typed
       back, manufacturing a bogus "newer" claim that would beat a colleague's real edit;
     · TOMBSTONES FALL OUT FREE — a record in the baseline and absent at save IS a deletion,
       so nothing needs wiring into the shared `pendingUndo` slot, and undo-then-save
       correctly produces no tombstone at all.
   The one semantic difference: rev means "changed since my last save", not "since my last
   keystroke". Since colleagues only ever see each other's SAVED files, those coincide.

   HOW IT WORKS. A BASELINE is captured whenever the portfolio is loaded or merged: for every
   record MERGE_SPEC knows about, its canonical content (key-sorted JSON, with rev/writer/del
   and local-only state stripped out). At save, `_revPlan` re-canonicalises and compares:
   changed or new records get `rev = (rev|0) + 1` and `writer = me`; unchanged records are
   left completely alone; keys in the baseline that have vanished become TOMBSTONES.

   ⚠ `_revPlan` MUTATES NOTHING. It returns a plan, because the save pipeline's step 3 is
   documented as building the bytes "without mutating anything" (v10.4 W1's four-step rule).
   `_revApply` is what writes the revs, and W4 will call it in step 4 — on success only, so a
   failed write cannot leave a rev bumped for bytes that never reached the disk.

   ⚠ LOCAL-ONLY STATE IS EXCLUDED, and this is not cosmetic: without it, changing your own
   sort order would bump the project's rev and let you clobber a colleague's project rename.
   `edits` is excluded too, on W1's finding that it is a pure mirror of the action fields. */

/* appMeta keys that are never a reason to claim a newer version. TWO categories, one list
   because the rule they need is identical:
     · per-machine UI state — without this, changing your own sort order would bump the
       project's rev and let you clobber a colleague's project rename;
     · ⚠ `lastSaved` — SAVE METADATA, not content, and found in W4a. It is shared in the file
       (the project header's "Last saved" date reads it), so it looks like content — but step 2
       of the save pipeline STAMPS it before step 3 plans, so leaving it in the diff made every
       save bump the active project's rev even when nothing else had changed. That is exactly
       the FALSE BUMP his Way A ruling chose this design to prevent: a no-op save would have
       manufactured a bogus "newer" claim and beaten a colleague's real edit to that project.
       The date still travels in the file; it simply cannot win an argument. */
const REV_LOCAL_APPMETA = ['uiState', 'stkView', 'pickerOpenOnly', 'gridDotColorByCategory',
                           'meetingTimer', 'sessionSnapshot', 'focusActionId', 'stakeholderSort',
                           'lastSaved'];
/* v18: of the rev-local keys, ONE is not personal. `sessionSnapshot` is the action baseline the
   latest meeting's `diff` is computed from at every save (see _captureAllFromDOM), and that diff is
   shared data — so the snapshot is TEAM state that merely never bumps a rev. With one server it
   stays in the shared project record, written through an unversioned side channel (last write
   wins, which is what the v14 merge did with it in effect). Every other rev-local key is one
   person's view state and lives in that person's preferences. */
const SHARED_UNVERSIONED_APPMETA = ['sessionSnapshot'];
/* Per-project keys that are derived or transient, so they are not the project's own content.
   ⚠⚠ v15.1: `_deletedIds` JOINS THE LIST, and it was producing a PHANTOM CHANGE ON EVERY SAVE.
   setActiveProject attaches it to the project record the moment you open one — a runtime Set
   the serialiser never writes (A65 documented exactly this for the import badge; the canon
   never got that fix). The baseline canon is adopted from disk-parsed records, which cannot
   carry it; _revCanonObj's JSON deep-clone turns the live Set into `{}` and kept the key — so
   the canons differed on every comparison, `_revPlan` stamped the project a rev FOR MERELY
   HAVING BEEN OPENED, and the ledger minted "changed project — _deletedIds changed" on every
   single save (five of five entries in his real log; v14.3/14.4 showed the same phantom as
   "description changed", which is what he had reported). ⚠ The post-save re-merge re-adopts
   from disk-parsed records, so the baseline LOST the key again after every save — that is why
   it fired every time, not once. Skipping it here kills the phantom rev, the phantom ledger
   row and the rev inflation at the single definition the merge, the supersede diff and the
   ledger all share. */
const REV_PROJECT_SKIP = ['edits', 'newActions', '_deletedIds'];

/* ══ v14 (W4a), v17.2 W3: WRITER IDENTITY (◆8) ════════════════════════════════════════════
   A writer is a PERSON ON A MACHINE, never a person alone: one human on two laptops must be
   TWO writers, or their own two devices re-create on a shared part exactly the fork this whole
   design exists to make impossible. So the id is `<user>.<machine>` — both now supplied by the
   launcher, with the machine half stable across browser profiles on the same computer.

   ⚠ COMPOSED BY PURE FUNCTIONS, and that is not tidiness: this block is extracted and eval'd
   under Node by qa/merge_props.js, so anything reaching for fetch(), indexedDB or window would
   make the property battery unrunnable. The async plumbing that FILLS the two values lives
   outside the extracted region — see _initWriterIdentity.

   ⚠ UNTIL IT RESOLVES THIS RETURNS 'local', and that is the reason _reseedMintCounters exists.
   The four per-project counters are seeded during boot, before the launcher can have answered,
   so ids minted from them would land in the WRONG writer's block. They are re-seeded the
   moment identity lands. Nothing else mints from a stored seed — the other five read
   _myWriterId() live at mint time. */
let _writerUser = null, _writerMachine = null;

/* [a-z0-9_-] only (◆8): this ends up INSIDE a filename, on Windows, under OneDrive's deep
   paths, and the roster classifies parts by pattern — so the character set is load-bearing
   rather than cosmetic. Bounded length for the same reason (MAX_PATH). */
function _sanitiseIdPart(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9_-]+/g, '-')
                                   .replace(/^-+|-+$/g, '').slice(0, 40);
}
/* Empty unless BOTH halves are known: a half-identity must never reach a file or a merge,
   because `user.` and `.machine` would both look like legitimate distinct writers. */
function _composeWriterId(user, machine) {
  const u = _sanitiseIdPart(user), m = _sanitiseIdPart(machine);
  return (u && m) ? (u + '.' + m) : '';
}
/* v18: one central server, so a writer is the logged-in USERNAME alone - the machine half existed
   only to keep two laptops from forking one shared part file, and there are no part files now.
   _composeWriterId stays for naming legacy part files (import / export-legacy). */
function _myWriterId() { return _sanitiseIdPart(_writerUser) || 'local'; }

/* The part-file this writer owns (◆8) — `TrendAI_FlightDeck_data.<user>.<machine>.js`.
   ⚠ The stem deliberately no longer shares a prefix with the legacy canonical
   `TrendAI_Programme_Management_Plan_data.js`, which is what makes the v14 clean break
   SELF-ENFORCING: the old file cannot be mistaken for a part by any pattern. */
const PART_STEM = 'TrendAI_FlightDeck_data';
function _partFileName(user, machine) {
  const w = _composeWriterId(user, machine);
  return w ? (PART_STEM + '.' + w + '.js') : '';
}
function _myPartFileName() { return _partFileName(_writerUser, _writerMachine); }

/* Deterministic JSON: object keys sorted, so two structurally equal records compare equal
   whatever order their keys happen to sit in. JSON.stringify alone would not guarantee that. */
function _revStable(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v === undefined ? null : v);
  if (Array.isArray(v)) { let s = '['; for (let i = 0; i < v.length; i++) { if (i) s += ','; s += _revStable(v[i]); } return s + ']'; }
  const ks = Object.keys(v).sort();
  let s = '{';
  for (let i = 0; i < ks.length; i++) { if (i) s += ','; s += JSON.stringify(ks[i]) + ':' + _revStable(v[ks[i]]); }
  return s + '}';
}

function _revDeletePath(obj, path) {
  const segs = path.split('.');
  let cur = obj;
  for (let i = 0; i < segs.length - 1; i++) { if (!cur || typeof cur !== 'object') return; cur = cur[segs[i]]; }
  if (cur && typeof cur === 'object') delete cur[segs[segs.length - 1]];
}

/* A record's own content, excluding its child collections (they are records in their own
   right), its bookkeeping keys, and anything local-only.
   ⚠ v14 (W7) split this in two WITHOUT changing what _revCanonOf returns. A supersede note has
   to name the FIELDS that differ between my discarded version and the winner, and it must use
   exactly this definition of "the record's own content" — otherwise it would report a child
   collection (which merges independently and was never lost) or a local-only key (which is not
   content at all) as something the user lost. One rule, two callers; a second copy of these
   deletions would be free to drift from the diff that drives the merge. */
function _revCanonObj(rec, kidsName) {
  const c = JSON.parse(JSON.stringify(rec));
  delete c.rev; delete c.writer; delete c.del;
  const kids = kidsName ? (MERGE_SPEC[kidsName] || []) : [];
  for (let i = 0; i < kids.length; i++) _revDeletePath(c, kids[i].path);
  if (kidsName === 'project') {
    for (let i = 0; i < REV_PROJECT_SKIP.length; i++) delete c[REV_PROJECT_SKIP[i]];
    if (c.appMeta && typeof c.appMeta === 'object')
      for (let i = 0; i < REV_LOCAL_APPMETA.length; i++) delete c.appMeta[REV_LOCAL_APPMETA[i]];
  }
  return c;
}
function _revCanonOf(rec, kidsName) {
  if (rec === null || typeof rec !== 'object') return _revStable(rec);
  return _revStable(_revCanonObj(rec, kidsName));
}

/* Walk every record MERGE_SPEC knows about, deepest-last, giving each a stable path key such
   as `projects/<uid>/actions/<id>`. ⚠ The SAME table drives the merge and this diff, so the
   two cannot disagree about what counts as a record. */
function _revWalk(db, visit) {
  for (let i = 0; i < MERGE_SPEC.root.length; i++) _revWalkColl(db, MERGE_SPEC.root[i], '', visit);
}
function _revWalkColl(parent, spec, prefix, visit) {
  const coll = _mergeGetPath(parent, spec.path);
  if (coll === undefined || coll === null) return;
  const base = prefix + spec.path + '/';
  if (spec.map) {
    if (typeof coll !== 'object' || Array.isArray(coll)) return;
    const ks = Object.keys(coll);
    for (let i = 0; i < ks.length; i++) visit(base + ks[i], coll[ks[i]], spec, null, coll, ks[i]);
    return;
  }
  if (!Array.isArray(coll)) return;
  for (let i = 0; i < coll.length; i++) {
    const rec = coll[i];
    const raw = rec ? rec[spec.key] : undefined;
    if (raw === undefined || raw === null || raw === '') continue;   /* the merge throws; the diff skips */
    const key = base + String(raw);
    visit(key, rec, spec, spec.kids, coll, String(raw));
    if (spec.kids) {
      const kids = MERGE_SPEC[spec.kids] || [];
      for (let j = 0; j < kids.length; j++) _revWalkColl(rec, kids[j], key + '/', visit);
    }
  }
}

/* Capture what the portfolio looked like when it was loaded or merged. */
function _revCaptureBaseline(db) {
  const m = new Map();
  _revWalk(db, function (key, rec, spec, kids) {
    /* ⚠⚠ v16.9 (W4, F3): `dispNo` IS HOP 1 OF 3, and naming only the ends of this chain is how
       a build drops it in the middle. baseline -> _revPlan -> _revTombRecords, all three.
       ⚠ It is captured OUTSIDE the canon on purpose: `displayNo` is already IN `_revCanonOf`
       (it is not in REV_PROJECT_SKIP), and it must STAY there - removing it would change
       revision stamping, merge winners and supersede notes portfolio-wide. This is a second,
       separate reading of the same field, for one purpose: surviving its own record.
       ⚠ Only programmes and projects ever have one; _dispNoOf returns 0 for everything else,
       so every other record in the walk stores `undefined` and nothing changes for them. */
    const _dn = _dispNoOf(rec);
    m.set(key, { canon: _revCanonOf(rec, kids), rev: rec && rec.rev | 0,
                 del: !!(rec && rec.del), coll: spec.path, keyField: spec.key || null,
                 dispNo: _dn > 0 ? _dn : undefined });
  });
  return m;
}

/* Compare the current portfolio against the baseline and return a PLAN. Mutates nothing. */
function _revPlan(db, baseline, writer) {
  const stamps = new Map();
  const seen = new Set();
  _revWalk(db, function (key, rec, spec, kids) {
    seen.add(key);
    if (!rec || typeof rec !== 'object') return;
    const base = baseline ? baseline.get(key) : undefined;
    if (base && base.canon === _revCanonOf(rec, kids)) return;       /* untouched: leave it alone */
    stamps.set(key, { rev: (rec.rev | 0) + 1, writer: writer });
  });
  /* In the baseline, gone now: a deletion. Skip any whose ancestor also vanished — one
     tombstone for a deleted project covers everything that was inside it. */
  const gone = [];
  if (baseline) baseline.forEach(function (v, key) { if (!seen.has(key) && !v.del) gone.push(key); });
  gone.sort();
  const tombs = [];
  for (let i = 0; i < gone.length; i++) {
    let covered = false;
    for (let j = 0; j < gone.length; j++)
      if (j !== i && gone[i].length > gone[j].length && gone[i].indexOf(gone[j] + '/') === 0) { covered = true; break; }
    if (covered) continue;
    const b = baseline.get(gone[i]);
    /* ⚠⚠ v16.9 (W4, F3): HOP 2 OF 3. _revTombRecords reads THIS, not the baseline, so a
       number added to the baseline alone would reach neither the plan nor the marker. */
    tombs.push({ key: gone[i], coll: b.coll, keyField: b.keyField,
                 id: gone[i].slice(gone[i].lastIndexOf('/') + 1),
                 rev: (b.rev | 0) + 1, writer: writer, dispNo: b.dispNo });
  }
  return { stamps: stamps, tombs: tombs };
}

/* Write a plan's revs onto the records. W4 calls this in the save pipeline's step 4, on
   success only. Returns how many records were stamped. */
function _revApply(db, plan) {
  let n = 0;
  _revWalk(db, function (key, rec) {
    const s = plan.stamps.get(key);
    if (!s || !rec || typeof rec !== 'object') return;
    rec.rev = s.rev; rec.writer = s.writer; n++;
  });
  return n;
}

/* The tombstone RECORDS a plan implies, ready to be folded into the collections that get
   serialised. Keyed with the collection's own key field so the merge groups them correctly.
   `id` is carried alongside because a MAP collection (the KPIs) is keyed by its own property
   name rather than by a field inside the record, so the folder below has nothing to read it
   from otherwise. */
function _revTombRecords(plan) {
  const out = [];
  for (let i = 0; i < plan.tombs.length; i++) {
    const t = plan.tombs[i];
    const r = { del: 1, rev: t.rev, writer: t.writer };
    if (t.keyField) r[t.keyField] = /^\d+$/.test(t.id) ? Number(t.id) : t.id;
    /* ⚠⚠ v16.9 (W4, F3): HOP 3 OF 3 - THE ONE STORED CHANGE IN THIS VERSION, and the only
       thing his schema consent of 2026-08-28 covers: a deletion marker now remembers the
       visible number its record held, so the next new record cannot be handed it.
       ⚠ LAZY, and that is load-bearing: a marker for a record that never had a number gets
       no key at all, so every marker written before this existed round-trips BYTE-IDENTICALLY
       and roundtrip_byte_exact does not move.
       ⚠ INTEGER-ONLY on the way in. The app's own minting only ever produces positive
       integers; refusing anything else here means a malformed file cannot put a fraction or a
       string into a marker THIS shell writes. Reading is guarded separately by _dispNoOf.
       ⚠ W1 measured this shape against v16.3 (production), v16.7 and v16.8: all three read it
       off the file, wrote it back, merged it, and went on numbering live records unchanged. */
    if (Number.isInteger(t.dispNo) && t.dispNo > 0) r.displayNo = t.dispNo;
    out.push({ path: t.key.slice(0, t.key.lastIndexOf('/')), id: t.id, rec: r });
  }
  return out;
}

/* ══ v14 (W4a): TOMBSTONES LIVE IN THE FILE, NEVER IN THE LIVE ARRAYS ══════════════════════
   A tombstone has to reach the part-file — it is the whole mechanism by which a delete beats a
   colleague's stale copy, and one that vanished would let their older record resurrect on the
   next merge. But it must NOT sit in the arrays the app renders from, or every renderer,
   counter and export in the shell would need a `!r.del` filter and the ones that were missed
   would show ghost rows.

   So the two directions are separated, and this is the pair that does it:
     · _revHarvestTombstones PULLS every `del` record out of a freshly loaded portfolio and
       hands them back, so the live arrays are clean the moment they are adopted;
     · _revFoldTombstones puts them back into the COPY that gets serialised, together with the
       ones the current save's plan just produced.
   ⚠ Both take a db and are otherwise pure, so the property battery drives them directly. Fold
   is only ever called on the staged copy _buildSaveDB owns — never on window.__DB__. */

function _revSpecIn(specs, collPath) {
  for (let i = 0; i < specs.length; i++) if (specs[i].path === collPath) return specs[i];
  return null;
}

/* Resolve a collection PATH ('projects', 'projects/<uid>/actions',
   'projects/<uid>/appMeta.changeRequests') to the container it names, walking MERGE_SPEC so
   the path grammar and the merge cannot disagree. Returns null when an ancestor record is not
   in this db — which is the normal case for a child of a deleted project, and correct: the
   parent's own tombstone already covers it.
   ⚠ `create` matters for one real case: deleting the LAST change request can leave the lazy
   `appMeta.changeRequests` key absent, and the tombstone still has to be written. Creating it
   on the staged copy is safe (it never touches live state) and the alternative is losing the
   deletion silently. */
function _revResolveColl(db, path, create) {
  const segs = path.split('/');
  let parent = db, specs = MERGE_SPEC.root;
  for (let i = 0; i < segs.length; i += 2) {
    const spec = _revSpecIn(specs, segs[i]);
    if (!spec) return null;
    if (i === segs.length - 1) {
      let coll = _mergeGetPath(parent, spec.path);
      if (coll === undefined || coll === null) {
        if (!create) return null;
        coll = spec.map ? {} : [];
        _mergeSetPath(parent, spec.path, coll);
      }
      return { coll: coll, spec: spec };
    }
    const coll = _mergeGetPath(parent, spec.path);
    if (!Array.isArray(coll)) return null;
    let rec = null;
    for (let j = 0; j < coll.length; j++)
      if (coll[j] && String(coll[j][spec.key]) === segs[i + 1]) { rec = coll[j]; break; }
    if (!rec) return null;
    parent = rec;
    specs = MERGE_SPEC[spec.kids] || [];
  }
  return null;
}

/* Take every `del` record OUT of a loaded portfolio. Returns them as {path, id, rec} — the
   same shape _revTombRecords yields, so the two streams concatenate. */
function _revHarvestTombstones(db) {
  const hits = [];
  _revWalk(db, function (key, rec, spec, kids, coll, keyStr) {
    if (rec && typeof rec === 'object' && rec.del) hits.push({ key: key, rec: rec, coll: coll, keyStr: keyStr });
  });
  /* Collected first, removed after: splicing during the walk would shift the indices it is
     still iterating. */
  const out = [];
  for (let i = 0; i < hits.length; i++) {
    const h = hits[i];
    out.push({ path: h.key.slice(0, h.key.lastIndexOf('/')), id: h.keyStr, rec: h.rec });
    if (Array.isArray(h.coll)) { const j = h.coll.indexOf(h.rec); if (j !== -1) h.coll.splice(j, 1); }
    else if (h.coll && typeof h.coll === 'object') delete h.coll[h.keyStr];
  }
  return out;
}

/* Fold tombstone records into the collections of `db` (always a staged copy). An id already
   present wins and the tombstone is dropped: a record that has been RE-CREATED under the same
   key is live again, and writing a tombstone beside it would give the merge two candidates for
   one key with no way to order them. Returns how many were folded in. */
function _revFoldTombstones(db, tombRecs) {
  let n = 0;
  for (let i = 0; i < tombRecs.length; i++) {
    const t = tombRecs[i];
    const found = _revResolveColl(db, t.path, true);
    if (!found) continue;                       /* ancestor gone — its own tombstone covers this */
    if (found.spec.map) {
      if (t.id === undefined || t.id === null) continue;
      if (!Object.prototype.hasOwnProperty.call(found.coll, String(t.id))) { found.coll[String(t.id)] = t.rec; n++; }
      continue;
    }
    if (!Array.isArray(found.coll)) continue;
    const key = found.spec.key, kv = String(t.rec[key]);
    let already = false;
    for (let j = 0; j < found.coll.length; j++)
      if (found.coll[j] && String(found.coll[j][key]) === kv) { already = true; break; }
    if (!already) { found.coll.push(t.rec); n++; }
  }
  return n;
}
/* ══ v14 (W3): WRITER-SCOPED ID MINTING — the trickiest correctness spot in the design ═════
   ⚠ NOTHING CALLS THIS YET. W3b replaces the seven mint paths and the five display
   formatters; until then the app mints exactly as before. DO NOT SWEEP.

   THE PROBLEM. Every nested id in this data is a PER-PROJECT COUNTER (W1's recon: actions,
   risks, stakeholders, assumptions, workstreams and sessions all restart at "1" in every
   project, benefits at "1" in every programme). So if two writers are offline and each adds a
   risk, both mint the same number — and the merge, which groups by (parent, id), would fuse
   two DIFFERENT records into one. Silent data loss, and the reason the scope calls this the
   trickiest spot.

   ⚠ WHY NOT A `uid` SIDECAR, the obvious answer that mirrors projects' id+uid. Because these
   ids are NUMBERS and the numeric substrate is deep: `edits` is keyed by action id, so are
   `deletedIds`, `dependsOn`, `relatedTo` and `parentId`, with `Number()` conversions
   throughout. A sidecar leaves the numeric `id` still colliding, so `dependsOn: [6]` would be
   ambiguous after a merge — it fixes identity while leaving every reference broken. Rejected.

   THE SCHEME. Give each writer a private BLOCK of one million numbers, derived from a hash of
   their writer id — deterministic, needing no coordination between machines, which is the
   whole constraint. An id is `block + counter`. Two writers therefore cannot collide even
   offline, and because ids stay NUMBERS, every reference above keeps working untouched and
   `MERGE_SPEC` needs no change at all.

   Block 0 is deliberately excluded so the smallest minted id is 1,000,001: single-writer-era
   ids are all far below that, so legacy and minted ids can never collide either. The top of
   the range is 2^22 blocks x 1e6 = 4.19e12, comfortably inside Number.MAX_SAFE_INTEGER
   (9.0e15), so no id can lose precision. Two writers share a block only on a full 32-bit hash
   collision: for the twelve writers this design is bounded at, about 1.7 in 100,000.

   NO REUSE, PRESERVED. The existing `*Seq` high-water marks exist so a number is never reused
   after the highest holder is deleted (RACI and comms reference stakeholder ids BY VALUE).
   That guarantee is kept — but a stored seq is honoured ONLY if it falls inside my own block.
   ⚠ That condition is load-bearing: `appMeta` rides with the project record, so a colleague's
   save can legitimately replace mine, and an out-of-block seq taken as a floor would push my
   next mint into THEIR block and manufacture exactly the collision this scheme prevents.
   Once W4 wires tombstones into Save, deleted ids survive as tombstones and the seq becomes
   redundant; retiring the six keys is a W8 removal, behind a census. */

const MINT_BLOCK_SIZE = 1000000;
const MINT_BLOCK_COUNT = 4194303;          /* 2^22 - 1, so block 0 is never issued */

function _mintBlock(writerId) {
  return ((_mergeHash32(String(writerId)) % MINT_BLOCK_COUNT) + 1) * MINT_BLOCK_SIZE;
}

/* The next id for this writer: one above the highest number already used INSIDE my block,
   counting the records present, any tombstones, and the legacy high-water mark.
   ── ⚠⚠ v16.8 (W5): TWO CORRECTIONS TO THE SENTENCE ABOVE, WHICH SWEEP-2 RECORDED AS C1.
   ⚠ "ANY TOMBSTONES" IS STILL FALSE and is left standing here as a known gap rather than
   quietly reworded: `_revAdoptBaseline` harvests tombstones OUT of the live collections before
   `_reseedMintCounters` runs, so the records this function walks never contain one. The
   protection against a tombstoned id being reissued comes from the stored mark below, not from
   counting markers.
   ✅ "THE LEGACY HIGH-WATER MARK" IS NOW TRUE OF ALL FOUR COUNTERS. When C1 was written only
   stakeholders were passed one — actions, meetings and workstreams got neither, which is
   exactly SWEEP-2's F-P4c-1. All four now pass their `appMeta.*Seq`. */
function _mintNextId(records, writerId, storedSeq) {
  const block = _mintBlock(writerId), top = block + MINT_BLOCK_SIZE;
  let hi = block;
  const consider = function (v) {
    const n = Number(v);
    if (isFinite(n) && n > hi && n > block && n < top) hi = n;
  };
  if (Array.isArray(records)) for (let i = 0; i < records.length; i++) {
    const r = records[i];
    consider(r && typeof r === 'object' ? (r.id !== undefined ? r.id : r.num) : r);
  }
  consider(storedSeq);
  return hi + 1;
}

/* The pretty number a register shows. ⚠ It is DERIVED, never stored: two writers offline both
   mint "the next risk", and only one of them can be shown as R-004. Deriving it from position
   in the id-sorted collection means every machine shows the same label for the same record,
   and the ugly six-figure block ids never reach a screen.
   Tombstones are skipped so a deleted row leaves no hole in the sequence. */
function _mintDisplayOrd(coll, rec) {
  if (!Array.isArray(coll)) return 1;
  const live = coll.filter(function (r) { return r && !r.del; })
                   .sort(function (a, b) { return _mergeCmpKey(a.id !== undefined ? a.id : a.num,
                                                               b.id !== undefined ? b.id : b.num); });
  let i = live.indexOf(rec);
  if (i === -1 && rec) {   /* a renderer may hand us a copy, so fall back to matching by key */
    const k = String(rec.id !== undefined ? rec.id : rec.num);
    i = live.findIndex(function (r) { return String(r.id !== undefined ? r.id : r.num) === k; });
  }
  return (i === -1 ? live.length : i) + 1;
}
/* @@REV_ENGINE_END@@ */

